/**
 * Lightweight, GDPR-friendly attribution beacon.
 *
 * Stores a persistent visitor_id in localStorage and fires a POST /api/attribution/beacon
 * on public page mounts. No third-party trackers, no cookies, no PII.
 */
import { api } from '@/lib/api';

const VISITOR_KEY = 'caelix_visitor_id';

const genVisitorId = () => {
  // 22-char base36-ish random — 128 bits of entropy is plenty
  const rand = (typeof crypto !== 'undefined' && crypto.getRandomValues)
    ? Array.from(crypto.getRandomValues(new Uint8Array(12))).map((b) => b.toString(36)).join('')
    : Math.random().toString(36).slice(2) + Math.random().toString(36).slice(2);
  return `vs_${rand}`.slice(0, 32);
};

export const getVisitorId = () => {
  try {
    let id = localStorage.getItem(VISITOR_KEY);
    if (!id) {
      id = genVisitorId();
      localStorage.setItem(VISITOR_KEY, id);
    }
    return id;
  } catch {
    return genVisitorId();
  }
};

const _fired = new Set();

export const trackPageView = (source, sourceRef) => {
  if (!source) return;
  const key = `${source}:${sourceRef || ''}`;
  // Client-side dedup within page-lifetime — server also stores multiple safely,
  // but this avoids double-firing on React 18 StrictMode dev double-mount.
  if (_fired.has(key)) return;
  _fired.add(key);

  const visitor_id = getVisitorId();
  api.post('/attribution/beacon', {
    visitor_id,
    source,
    source_ref: sourceRef || null,
    event_kind: 'page_view',
  }).catch(() => { /* silent — analytics never blocks UX */ });
};
